/**
 * The back-office navigation, as specified by REQ-015 AC-005a.
 *
 * The eight sections are the shape of the back office whether or not the pages
 * behind them exist. A page that is not built yet is listed with
 * `available: false` and drawn disabled rather than left out — a menu that
 * grows silently as work lands gives nobody a way to see what is still
 * missing, and progress should be readable off the navigation.
 *
 * Roles are Keycloak realm roles (REQ-015, "The role model"). This is display
 * gating only; every back-office endpoint enforces the same roles server-side,
 * which is where it counts.
 */
import {
  BarChart3,
  Bot,
  Building2,
  Headphones,
  Megaphone,
  Settings2,
  Target,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import {
  groupLabel,
  groupPath,
  METRIC_GROUPS,
} from "@/pages/admin/dashboard/metrics";

/** ComDesk's surfaces, as `[hash route, label]`, in menu order: Dashboard first. */
export const COMDESK_SURFACES: ReadonlyArray<readonly [string, string]> = [
  ["dashboard", "Dashboard"],
  ["contacts", "Contacts"],
  ["mail", "Mail"],
  ["phone", "Phone"],
  ["calendar", "Calendar"],
  ["todos", "Todos"],
  ["chat", "Chat"],
  ["video", "Video"],
];

export interface BackOfficePage {
  label: string;
  path: string;
  /** False while the page is specified but not built. Drawn disabled. */
  available: boolean;
}

export interface BackOfficeSection {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Any one of these realm roles is enough to see the section. */
  roles: readonly string[];
  /** Where the section itself goes, for a section that is a single page. */
  path?: string;
  pages: BackOfficePage[];
  /** The requirement that specifies this section, for the next reader. */
  specifiedBy: string;
}

/** Every staff role, for sections open to all of them. */
const ANY_STAFF = [
  "staff_admin",
  "staff_marketing",
  "staff_sales",
  "staff_finance",
  "staff_support",
] as const;

export const BACK_OFFICE_SECTIONS: BackOfficeSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: BarChart3,
    roles: ANY_STAFF,
    path: "/admin/dashboard",
    specifiedBy: "REQ-021",
    pages: [
      // The counted totals that were here before REQ-021, kept: they are the
      // hub the back office lands on.
      { label: "Overview", path: "/admin/dashboard", available: true },
      // At most ten figures, chosen by the metric registry rather than by this
      // menu (REQ-021 AC-003).
      {
        label: "Executive",
        path: "/admin/dashboard/executive",
        available: true,
      },
      // AC-004. Beside the executive screen rather than under it: the
      // aggregate and the cohorts answer different questions, and the second
      // is the one that shows whether newer months behave better.
      {
        label: "Cohorts",
        path: "/admin/dashboard/cohorts",
        available: true,
      },
      // AC-001. Listed beside the figures because a definition nobody can
      // find is a definition nobody checks.
      {
        label: "Definitions",
        path: "/admin/dashboard/definitions",
        available: true,
      },
      // The per-system screens, one per group in the metric registry and
      // generated from it rather than typed out here: a group added to the
      // registry gets a menu entry, so no metric can be added and left
      // unreachable. They come last on purpose — the executive screen stays
      // second, where somebody opening the back office meets it first, and
      // seven system names above it would bury the screen everybody wants.
      ...METRIC_GROUPS.map((group) => ({
        label: groupLabel(group),
        path: groupPath(group),
        available: true,
      })),
    ],
  },
  {
    id: "comdesk",
    label: "ComDesk",
    icon: Headphones,
    roles: ["staff_admin", "staff_sales", "staff_support"],
    specifiedBy: "REQ-020",
    // BIZZMATIX ComDesk, framed in the content area (pages/admin/comdesk/
    // ComDeskFrame.tsx). These are its surfaces as of 3.4.2, Dashboard first;
    // the menu replaces them with ComDesk's own manifest once it has loaded
    // (hooks/useComDeskMenu.ts), so a surface a later version adds appears
    // without a change here.
    pages: COMDESK_SURFACES.map(([surface, label]) => ({
      label,
      path: `/admin/comdesk/${surface}`,
      available: true,
    })),
  },
  {
    id: "customers",
    label: "Customers",
    icon: Building2,
    roles: ["staff_admin", "staff_sales", "staff_support"],
    specifiedBy: "REQ-018",
    pages: [
      {
        label: "Travellers",
        path: "/admin/customers/travellers",
        available: true,
      },
      // One partner record for every kind of partner (REQ-018). Insurers,
      // tour operators, medical societies, creators and clinics differ in what
      // they earn, not in what they are, so they are one list with a type
      // rather than three pages that cannot be counted together.
      { label: "Partners", path: "/admin/customers/partners", available: true },
      // Insurers' offers to travellers, and the conversions they report
      // (REQ-025). Staff-managed in phase 1; partners do not edit them.
      {
        label: "Partner offers",
        path: "/admin/customers/partner-offers",
        available: true,
      },
      // The three older pages, kept reachable. They are the partner-facing
      // surfaces rather than staff views, and folding those in is the portal
      // half of REQ-018 (AC-008), which is not built.
      {
        label: "Healthcare providers",
        path: "/admin/healthcare",
        available: true,
      },
      { label: "Tour operators", path: "/admin/tours", available: true },
      {
        label: "Affiliate partners",
        path: "/admin/affiliates",
        available: true,
      },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    icon: Target,
    roles: ["staff_admin", "staff_sales"],
    specifiedBy: "REQ-017",
    pages: [
      { label: "Lead Pipeline", path: "/admin/sales/leads", available: true },
      {
        label: "Target Pipeline",
        path: "/admin/sales/targets",
        available: true,
      },
      // Inbound already, and already built: a demo booking is a lead from the
      // landing page. REQ-017 absorbs it into the lead pipeline.
      { label: "Demo bookings", path: "/admin/demo-bookings", available: true },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Megaphone,
    roles: ["staff_admin", "staff_marketing"],
    specifiedBy: "REQ-016",
    pages: [
      { label: "SEO", path: "/admin/marketing/seo", available: true },
      // Built, and honest about being idle: it samples nothing until an
      // assistant API key is set, and the page says so rather than drawing an
      // empty series that reads as "never mentioned" (REQ-016 AC-005).
      { label: "AIO", path: "/admin/marketing/aio", available: true },
      {
        label: "Campaigns",
        path: "/admin/marketing/campaigns",
        available: true,
      },
      // Every blog entry, and the approval that publishes one. The pipeline
      // writes drafts; nothing goes live without a named reviewer here
      // (MAR-003, REQ-016 AC-010).
      {
        label: "Content",
        path: "/admin/marketing/content",
        available: true,
      },
      // Home page to payment, by channel, counted without cookies
      // (go-to-market blocker 7).
      {
        label: "Launch funnel",
        path: "/admin/marketing/funnel",
        available: true,
      },
      // What each search phrase brings, and whether pages are indexed
      // (REQ-019 FR-019-13), drawn by the growth engine every morning.
      {
        label: "Keyword impact",
        path: "/admin/marketing/keywords",
        available: true,
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: Wallet,
    roles: ["staff_admin", "staff_finance"],
    specifiedBy: "REQ-019",
    pages: [
      // One screen rather than four. Under a merchant of record the Stripe
      // balance, the bank reconciliation and the incoming invoices are the
      // same question asked three ways — did the money arrive, and what do we
      // owe — and splitting them made each half an answer (REQ-019).
      { label: "Money", path: "/admin/finance", available: true },
      { label: "Tax", path: "/admin/finance/tax", available: false },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    icon: Bot,
    roles: ["staff_admin"],
    specifiedBy: "REQ-022",
    pages: [
      // One surface, not seven. The registry, the run log, the budgets and
      // the stop are the same subject — what an agent may do and who is
      // watching — and splitting them across pages would let somebody enable
      // an agent on one screen without seeing the queue it feeds on another
      // (REQ-022).
      { label: "Supervision", path: "/admin/agents", available: true },
      // The evaluation half of REQ-022 (AC-006, AC-007): case sets, offline
      // scoring and regression gating. Not built.
      {
        label: "Evaluation",
        path: "/admin/agents/evaluation",
        available: false,
      },
    ],
  },
  {
    id: "platform",
    label: "Platform Admin",
    icon: Settings2,
    roles: ["staff_admin"],
    specifiedBy: "REQ-015",
    pages: [
      // The one page that exists. It lists the application's users; staff
      // management, invitations and role assignment are REQ-015's remaining
      // work and are not the same screen.
      { label: "Users", path: "/admin/users", available: true },
      // Invitations, role assignment and the audit trail on one screen: they
      // are the same subject — who works here and what they did — and granting
      // a role on one page while the trail sits on another is how a change
      // goes unexamined (REQ-015 §7).
      {
        label: "People & audit",
        path: "/admin/platform",
        available: true,
      },
      {
        label: "Organisation",
        path: "/admin/platform/organisation",
        available: false,
      },
      // REQ-015 AC-005b: no requirement covers a risk register, a threat model
      // or a mitigation tracker. This stays disabled until one is written —
      // being listed is how the gap stays visible.
      {
        label: "Compliance & risks",
        path: "/admin/platform/compliance",
        available: false,
      },
    ],
  },
];

/**
 * The URL the section itself owns — what somebody types, bookmarks or lands on
 * from outside the menu. Derived from the id where a section does not name one,
 * because the id is the URL segment; `anti-orphan` in adminNavigation.test.ts
 * fails if a section's base path resolves to no route, which is what happened
 * to /admin/comdesk: it fell through App.tsx's catch-all and threw a staff
 * member onto the traveller dashboard, reading as a lost session.
 */
export function sectionBasePath(section: BackOfficeSection): string {
  return section.path ?? `/admin/${section.id}`;
}

/**
 * Where a section opens: its first built page. A section whose pages are all
 * still unbuilt has nowhere honest to land, and returns null rather than a URL
 * that would fall through — its header only expands, and the menu shows the
 * unbuilt pages disabled (REQ-015 AC-005a).
 */
export function sectionLandingPath(section: BackOfficeSection): string | null {
  return section.pages.find((page) => page.available)?.path ?? null;
}

/** The sections a holder of these realm roles may see. */
export function sectionsForRoles(held: string[]): BackOfficeSection[] {
  return BACK_OFFICE_SECTIONS.filter((section) =>
    section.roles.some((role) => held.includes(role)),
  );
}
